import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { addDays, addMonths, format as formatDate, nextFriday, nextMonday, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { AlarmClockCheck, ArrowUp, BellDot, Paperclip, X } from "lucide-react";
import { type KeyboardEvent, useRef, useState } from "react";

import type { CreateClaimNotePayload } from "@upclaim/sdk/src/data-contracts";

import { DragDropTextarea, type DragDropTextareaHandle } from "@/components/drag-drop-textarea";
import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import { fileToPayload } from "@/components/files/use-file-to-payload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AdminCombobox } from "@/features/search/components/admin-combobox";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import adminAPI from "@/services/api";

import { useCreateClaimNote, useUpdateClaimNote } from "../../api/use-claim-mutations";
import { extractMentionAdminIds } from "./timeline-utils";

/** Shape actually returned by GET /admin/admins (the SDK types it as object[]). */
interface AdminListItem {
  id: string;
  fullName: string;
  email?: string | null;
  profilePictureUrl?: string | null;
}

interface DatePreset {
  label: string;
  date: Date;
}

/** The 8 French date presets of the reminder dialog (Vue ClaimNoteEditor.datePresets). */
function buildDatePresets(): DatePreset[] {
  const today = startOfDay(new Date());
  return [
    { label: "Demain", date: addDays(today, 1) },
    { label: "Dans 3 jours", date: addDays(today, 3) },
    { label: "Lundi prochain", date: nextMonday(today) },
    { label: "Vendredi prochain", date: nextFriday(today) },
    { label: "Dans 1 semaine", date: addDays(today, 7) },
    { label: "Dans 2 semaines", date: addDays(today, 14) },
    { label: "Dans 1 mois", date: addMonths(today, 1) },
    { label: "Dans 2 mois", date: addMonths(today, 2) },
  ];
}

/** Note-attachment shape of the create/update note payloads (raw base64). */
async function fileToNoteAttachment(file: File) {
  const { filename, fileData, mimeType, size } = await fileToPayload(file);
  return { filename, fileData, mimeType, size };
}

interface ClaimNoteEditorProps {
  claimId: string;
  noteId?: string;
  initialContent?: string;
  initialFile?: PreviewFile | null;
  editMode?: boolean;
  onCancel?: () => void;
  onSaved?: () => void;
}

/** Port of timeline/ClaimNoteEditor.vue. */
export function ClaimNoteEditor({
  claimId,
  noteId,
  initialContent,
  initialFile = null,
  editMode = false,
  onCancel,
  onSaved,
}: ClaimNoteEditorProps) {
  const { user: auth0User } = useAuth0();

  const [noteContent, setNoteContent] = useState(initialContent ?? "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [removeExistingFile, setRemoveExistingFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Task/Reminder state
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [isReminderConfirmed, setIsReminderConfirmed] = useState(false);
  const [taskDueDate, setTaskDueDate] = useState<Date | null>(null);
  const [customDateTimeInput, setCustomDateTimeInput] = useState("");
  const [selectedPresetLabel, setSelectedPresetLabel] = useState<string | null>(null);
  const [taskAssignedAdminId, setTaskAssignedAdminId] = useState<string | null>(null);
  const [taskCategoryId, setTaskCategoryId] = useState<number | null>(1);
  const [sendSlackNotification, setSendSlackNotification] = useState(true);

  const textareaRef = useRef<DragDropTextareaHandle>(null);

  // Admin list for mention matching + reminder assignment defaults.
  const { data: adminList = [] } = useQuery({
    queryKey: queryKeys.search.admins,
    queryFn: async () => (await adminAPI.listAdmin()) as AdminListItem[],
  });

  const { data: categories = [] } = useQuery({
    queryKey: [...queryKeys.claims.all, "task-categories"],
    queryFn: () => adminAPI.listTaskCategories(),
  });

  const createNote = useCreateClaimNote(claimId);
  const updateNote = useUpdateClaimNote(claimId);
  const isSubmitting = createNote.isPending || updateNote.isPending;

  const currentUserEmail = auth0User?.email?.toLowerCase();
  const currentUserAdmin = currentUserEmail
    ? (adminList.find((admin) => admin.email?.toLowerCase() === currentUserEmail) ?? null)
    : null;
  const assignedAdminData = taskAssignedAdminId
    ? (adminList.find((admin) => admin.id === taskAssignedAdminId) ?? null)
    : null;

  const canSubmit = (noteContent.trim().length > 0 || !!selectedFile) && !isSubmitting;
  const formattedReminderDate = taskDueDate ? formatDate(taskDueDate, "dd/MM/yyyy", { locale: fr }) : "";
  const datePresets = buildDatePresets();

  /** Mirrors the Vue watcher: default the assignee to the current user when a date is picked. */
  function defaultAssigneeIfNeeded() {
    if (!taskAssignedAdminId && currentUserAdmin) {
      setTaskAssignedAdminId(currentUserAdmin.id);
    }
  }

  function selectDatePreset(preset: DatePreset) {
    setTaskDueDate(preset.date);
    setSelectedPresetLabel(preset.label);
    setCustomDateTimeInput("");
    defaultAssigneeIfNeeded();
  }

  function onCustomDateTimeChange(value: string) {
    setCustomDateTimeInput(value);
    if (value) {
      setTaskDueDate(new Date(value));
      setSelectedPresetLabel(null);
      defaultAssigneeIfNeeded();
    }
  }

  function clearReminder() {
    setTaskDueDate(null);
    setCustomDateTimeInput("");
    setSelectedPresetLabel(null);
    setTaskAssignedAdminId(null);
    setTaskCategoryId(1);
    setSendSlackNotification(true);
    setIsReminderConfirmed(false);
  }

  function resetForm() {
    setNoteContent("");
    setSelectedFile(null);
    clearReminder();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submitNote();
      return;
    }
    // "/" opens the reminder dialog (only if the textarea is empty; "@" reserved for mentions)
    if (event.key === "/" && !editMode && noteContent === "") {
      event.preventDefault();
      setIsReminderModalOpen(true);
      return;
    }
    if (event.key === "Escape") {
      (event.target as HTMLElement | null)?.blur();
    }
  }

  async function submitNote() {
    if (!canSubmit) {
      return;
    }

    const mentions = extractMentionAdminIds(noteContent, adminList);

    if (editMode && noteId) {
      const payload: Parameters<typeof adminAPI.updateClaimNote>[2] = { content: noteContent };
      if (removeExistingFile) {
        payload.attachment = null;
      } else if (selectedFile) {
        payload.attachment = await fileToNoteAttachment(selectedFile);
      }
      updateNote.mutate(
        { noteId, payload },
        {
          onSuccess: ({ success }) => {
            if (success) {
              onSaved?.();
            }
          },
        },
      );
      return;
    }

    const payload: CreateClaimNotePayload = { content: noteContent, mentions };
    if (selectedFile) {
      payload.attachment = await fileToNoteAttachment(selectedFile);
    }

    if (taskDueDate) {
      const assignedAdminId = taskAssignedAdminId ?? currentUserAdmin?.id;
      if (!assignedAdminId) {
        notify.error("Veuillez sélectionner un admin pour le rappel");
        return;
      }
      payload.task = {
        title: noteContent,
        dueDate: taskDueDate,
        assignedAdminId,
        // Always SLACK_REMINDER: the only reminder value the API accepts —
        // Vue's non-Slack path sent "REMINDER" and got a 400.
        type: "SLACK_REMINDER",
        categoryId: taskCategoryId ?? 1,
      };
    }

    createNote.mutate(payload, {
      onSuccess: ({ success }) => {
        if (success) {
          resetForm();
        }
      },
    });
  }

  function handleLocalFilePreview() {
    if (selectedFile) {
      setPreviewFile({
        id: "preview",
        name: selectedFile.name,
        mimeType: selectedFile.type,
        size: selectedFile.size,
        previewUrl: URL.createObjectURL(selectedFile),
      });
      setIsPreviewOpen(true);
    }
  }

  function closePreview() {
    if (previewFile?.previewUrl) {
      URL.revokeObjectURL(previewFile.previewUrl);
    }
    setIsPreviewOpen(false);
    setPreviewFile(null);
  }

  return (
    <div className="px-4 pb-6">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-100 focus-within:border-gray-400 focus-within:ring-2 focus-within:ring-black/10 focus-within:ring-offset-1">
        <DragDropTextarea
          ref={textareaRef}
          value={noteContent}
          onValueChange={setNoteContent}
          file={selectedFile}
          onFileChange={setSelectedFile}
          placeholder="Ajouter une note interne... (Entrée pour envoyer)"
          rows={1}
          onSubmit={() => void submitNote()}
          onFileClick={handleLocalFilePreview}
          onKeyDown={handleKeyDown}
        />

        {editMode && initialFile && !removeExistingFile && !selectedFile ? (
          <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2">
            <div className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs">
              <Paperclip className="h-3 w-3 text-gray-500" />
              <span>{initialFile.name}</span>
            </div>
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    onClick={() => setRemoveExistingFile(true)}
                    className="text-xs text-red-600 hover:text-red-800"
                  />
                }
              >
                Supprimer
              </TooltipTrigger>
              <TooltipContent>Supprimer la pièce jointe</TooltipContent>
            </Tooltip>
          </div>
        ) : null}

        {isReminderConfirmed && taskDueDate ? (
          <div className="flex items-center justify-between border-t border-blue-100 bg-blue-50 px-3 py-2">
            <button
              type="button"
              onClick={() => setIsReminderModalOpen(true)}
              className="flex cursor-pointer items-center gap-2 text-xs text-blue-700 hover:text-blue-800"
            >
              <BellDot className="h-3.5 w-3.5" />
              <span>
                Rappel programmé le {formattedReminderDate}{" "}
                {sendSlackNotification ? (
                  <span className="font-semibold italic">(Rappel Slack activé)</span>
                ) : null}
              </span>
            </button>
            <button
              type="button"
              onClick={clearReminder}
              className="text-blue-600 hover:text-blue-800"
              aria-label="Annuler le rappel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        <div className="flex items-center justify-between border-t border-gray-100 bg-white px-3 py-2">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    onClick={() => textareaRef.current?.triggerFileUpload()}
                    className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Joindre un fichier"
                  />
                }
              >
                <Paperclip className="h-4 w-4 -rotate-45 transform" />
              </TooltipTrigger>
              <TooltipContent>Joindre un fichier</TooltipContent>
            </Tooltip>

            {!editMode ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      onClick={() => setIsReminderModalOpen(true)}
                      className={cn(
                        "rounded-md p-1.5",
                        isReminderConfirmed
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "text-gray-400 hover:bg-gray-100 hover:text-gray-600",
                      )}
                      aria-label={isReminderConfirmed ? "Modifier le rappel" : "Créer un rappel"}
                    />
                  }
                >
                  <AlarmClockCheck className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>
                  {isReminderConfirmed ? "Modifier le rappel" : "Créer un rappel (ou tapez /)"}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            {!editMode ? (
              <div className="flex items-center gap-2">
                {taskDueDate && assignedAdminData ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        assignedAdminData.profilePictureUrl ? (
                          <img
                            src={assignedAdminData.profilePictureUrl}
                            alt={assignedAdminData.fullName}
                            className="h-6 w-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200">
                            <span className="text-xs font-medium text-gray-600">
                              {assignedAdminData.fullName?.charAt(0)}
                            </span>
                          </div>
                        )
                      }
                    />
                    <TooltipContent>Rappel assigné à {assignedAdminData.fullName}</TooltipContent>
                  </Tooltip>
                ) : currentUserAdmin ? (
                  currentUserAdmin.profilePictureUrl ? (
                    <img
                      src={currentUserAdmin.profilePictureUrl}
                      alt="Admin"
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200">
                      <span className="text-xs font-medium text-gray-600">
                        {currentUserAdmin.fullName?.charAt(0)}
                      </span>
                    </div>
                  )
                ) : null}
              </div>
            ) : null}

            {editMode ? (
              <Button variant="outline" size="sm" onClick={onCancel}>
                Annuler
              </Button>
            ) : null}

            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    onClick={() => void submitNote()}
                    disabled={!canSubmit}
                    className="rounded-md bg-black p-1.5 text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={editMode ? "Enregistrer les modifications" : "Publier la note"}
                  />
                }
              >
                <ArrowUp className="h-4 w-4" />
              </TooltipTrigger>
              <TooltipContent>
                {editMode ? "Enregistrer les modifications" : "Publier la note"}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <FilePreviewDialog open={isPreviewOpen} file={previewFile} onClose={closePreview} />

      <Dialog open={isReminderModalOpen} onOpenChange={setIsReminderModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Programmer un rappel</DialogTitle>
          </DialogHeader>

          <DialogPanel>
            <div className="space-y-6">
              {/* biome-ignore lint/a11y/useSemanticElements: mirrors the Vue markup */}
              <div role="group" aria-labelledby="reminder-date-label">
                <h3 id="reminder-date-label" className="mb-3 text-sm font-semibold text-gray-900">
                  Date du rappel
                </h3>
                {/* biome-ignore lint/a11y/useSemanticElements: mirrors the Vue markup */}
                <div className="grid grid-cols-2 gap-2" role="group">
                  {datePresets.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => selectDatePreset(preset)}
                      aria-pressed={selectedPresetLabel === preset.label}
                      aria-label={`Programmer pour ${preset.label.toLowerCase()}`}
                      className={cn(
                        "rounded-md border px-3 py-2 text-left text-sm font-medium transition-colors",
                        selectedPresetLabel === preset.label
                          ? "border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-600"
                          : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                      )}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="mt-4">
                  <Label
                    htmlFor="custom-datetime-note"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Ou choisir une date et heure précise
                  </Label>
                  <input
                    id="custom-datetime-note"
                    type="datetime-local"
                    value={customDateTimeInput}
                    onChange={(event) => onCustomDateTimeChange(event.target.value)}
                    className="block w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <AdminCombobox
                value={taskAssignedAdminId}
                onChange={setTaskAssignedAdminId}
                label="Assigner à"
                placeholder="Sélectionner un admin"
              />

              <div>
                <Label className="block text-sm font-medium text-gray-900">Catégorie</Label>
                <Select
                  value={taskCategoryId !== null ? String(taskCategoryId) : null}
                  onValueChange={(value) => setTaskCategoryId(value ? Number(value) : null)}
                >
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue>
                      {categories.find((category) => category.id === taskCategoryId)?.displayName ??
                        "Sélectionner une catégorie"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={String(category.id)}>
                        {category.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3">
                <Switch checked={sendSlackNotification} onCheckedChange={setSendSlackNotification} />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">Recevoir un rappel sur</span>
                  <img
                    src="https://ucarecdn.com/5d44d1d4-d8f6-4ce3-9d44-1e8adfaab88f/Slack_RGB.png"
                    alt="Slack"
                    className="-mx-3 h-10 object-contain"
                  />
                </div>
              </div>
            </div>
          </DialogPanel>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReminderModalOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (taskDueDate) {
                  setIsReminderConfirmed(true);
                }
                setIsReminderModalOpen(false);
              }}
            >
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
